import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { map, Observable } from 'rxjs'
import { environment } from '../../environments/environment'
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request'
import { Operator } from '../store/interfaces/operator'
import { ApiService } from './api.service'
import { unwrapCrudFirstItem, unwrapCrudList, type CrudListResponse } from '../common/utils/crud-response.util'
import { DataScope } from '../permissions/permission.types'
import { RegistrationStatusFilter } from '../common/utils/registration-status.util'
@Injectable({
  providedIn: 'root',
})
export class OperatorService {
  private readonly resource = 'operators'
  private readonly base = `${environment.apiUrl}/`

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
  ) {}

  getOperatorById(id: string | number): Observable<Operator> {
    const query = RequestQueryBuilder.create({
      join: [{ field: 'userBase' }],
    })
    return this.apiService.getById<Operator>(this.resource, id, query)
  }

  getListPaginated(
    filters: {
      nameSearch?: string
      firstName?: string
      lastName?: string
      email?: string
      page?: number
      limit?: number
      dataScope?: DataScope
      contractIds?: string[]
      registrationStatus?: RegistrationStatusFilter
    } = {},
  ): Observable<{ data: Operator[]; count: number; total: number; page: number; pageCount: number }> {
    let params = new HttpParams()
    if (filters.nameSearch?.trim()) params = params.set('nameSearch', filters.nameSearch.trim())
    if (filters.firstName) params = params.append('filter', `firstName||$contL||${filters.firstName}`)
    if (filters.lastName) params = params.append('filter', `lastName||$contL||${filters.lastName}`)
    if (filters.email) params = params.append('filter', `email||$contL||${filters.email}`)

    const registrationStatus = filters.registrationStatus ?? ''
    if (registrationStatus === 'inactive') {
      params = params.append('filter', 'isDisabled||$eq||true')
    } else {
      if (registrationStatus === 'pending') {
        params = params.append('filter', 'mustSetPasswordNextLogin||$eq||true')
      } else if (registrationStatus === 'active') {
        params = params.append('filter', 'mustSetPasswordNextLogin||$eq||false')
      }
    }
    if (filters.dataScope) params = params.set('dataScope', filters.dataScope)
    params = params.append('join', 'operatorRoles')
    params = params.append('join', 'operatorRoles.role')
    params = params.append('join', 'userBase')
    params = params.set('sort', 'firstName,ASC')
    params = params.set('limit', String(filters.limit ?? 20))
    params = params.set('page', String(filters.page ?? 1))
    return this.http.get<any>(`${this.base}${this.resource}`, { params }).pipe(
      map((res) => {
        if (Array.isArray(res)) {
          return {
            data: res as Operator[],
            count: res.length,
            total: res.length,
            page: Number(filters.page ?? 1),
            pageCount: 1,
          }
        }

        const data = Array.isArray(res?.data) ? (res.data as Operator[]) : []
        return {
          data,
          count: Number(res?.count ?? data.length),
          total: Number(res?.total ?? data.length),
          page: Number(res?.page ?? filters.page ?? 1),
          pageCount: Number(res?.pageCount ?? 1),
        }
      }),
    )
  }

  getOperatorByUserBaseId(userBaseId: string | number): Observable<Operator | null> {
    const query = RequestQueryBuilder.create({
      search: {
        userBaseId: {
          [CondOperator.EQUALS]: userBaseId,
        },
      },
      join: [{ field: 'operatorRoles' }],
    })

    return this.apiService
      .get<CrudListResponse<Operator>>(this.resource, query)
      .pipe(map((res) => unwrapCrudFirstItem(res)))
  }

  getActiveOperators(): Observable<Operator[]> {
    const query = RequestQueryBuilder.create({
      search: {
        isDisabled: {
          [CondOperator.EQUALS]: false,
        },
      },
      sort: {
        field: 'firstName',
        order: 'ASC',
      },
      join: [{ field: 'operatorRoles' }],
    })

    return this.apiService.get<CrudListResponse<Operator>>(this.resource, query).pipe(map((res) => unwrapCrudList(res)))
  }

  updateOperator(id: string | number, body: Partial<Operator>): Observable<Operator> {
    return this.http.patch<Operator>(`${this.base}${this.resource}/${id}`, body)
  }
}
